import { useCallback, useEffect, useState } from "react";
import {
  getSources, getSourcesStats, uploadSource, ingestSources,
  type SourceDocument, type SourcesStats, type IngestResult, type UploadResult,
} from "../api/admin";
import Icon from "../components/icons";

// Ortak mevzuat havuzu (corpus). Tüm müşteriler aynı index'ten faydalanır.
export default function Corpus() {
  const [stats, setStats] = useState<SourcesStats | null>(null);
  const [sources, setSources] = useState<SourceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [uploadSubdir, setUploadSubdir] = useState("");
  const [autoIngest, setAutoIngest] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [uploadLog, setUploadLog] = useState<UploadResult[]>([]);
  const [ingestResult, setIngestResult] = useState<IngestResult | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, st] = await Promise.all([
        getSources().catch(() => []),
        getSourcesStats().catch(() => null),
      ]);
      setSources(docs);
      setStats(st);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onUploadFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setErr(null);
    const results: UploadResult[] = [];
    try {
      for (const f of Array.from(files)) {
        results.push(await uploadSource(f, uploadSubdir || undefined));
      }
      setUploadLog((prev) => [...results, ...prev].slice(0, 20));
      setOkMsg(`${results.length} dosya yüklendi.`);
      setTimeout(() => setOkMsg(null), 4000);
      void load();
      if (autoIngest && results.length > 0) {
        setIngesting(true);
        try {
          setIngestResult(await ingestSources({ only: results.map((u) => u.name) }));
        } finally {
          setIngesting(false);
        }
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }, [uploadSubdir, autoIngest, load]);

  const onIngest = useCallback(async (reset: boolean) => {
    if (reset && !window.confirm("Tüm indeks silinip yeniden oluşturulacak. Emin misiniz?")) return;
    setIngesting(true);
    setErr(null);
    setIngestResult(null);
    try {
      const r = await ingestSources({ reset });
      setIngestResult(r);
      setOkMsg(`${r.indexed} dosya · ${r.chunks} chunk indekslendi.`);
      setTimeout(() => setOkMsg(null), 6000);
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setIngesting(false);
    }
  }, [load]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Mevzuat Havuzu (Corpus)</h1>
        <p className="page-sub">Tüm müşterilerin paylaştığı RAG corpus'u: doküman yükleme ve indeksleme.</p>
      </div>
      <p className="muted" style={{ marginBottom: 16 }}>
        Ortak mevzuat indeksine doküman yükleyin ve indeksleyin. <strong>Tüm müşteriler</strong> bu
        havuzdan faydalanır.
      </p>

      {err && <div className="error">{err}</div>}
      {okMsg && <div className="notice">{okMsg}</div>}

      {stats && (
        <div className="corpus-stats" style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div className="card" style={{ textAlign: "center", minWidth: 120, marginBottom: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{stats.files}</div>
            <div className="muted" style={{ fontSize: 12 }}>dosya</div>
          </div>
          <div className="card" style={{ textAlign: "center", minWidth: 120, marginBottom: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{stats.chunks.toLocaleString()}</div>
            <div className="muted" style={{ fontSize: 12 }}>chunk</div>
          </div>
          <div className="card" style={{ textAlign: "center", minWidth: 160, marginBottom: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, padding: "5px 0" }}>
              {stats.is_indexing ? "⏳ indeksleniyor" : "✓ hazır"}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>{stats.index}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="cf-grid">
          <label className="cf-field">
            <span>Alt klasör</span>
            <select value={uploadSubdir} onChange={(e) => setUploadSubdir(e.target.value)} disabled={uploading}>
              <option value="">data/ (kök)</option>
              <option value="standartlar">standartlar</option>
              <option value="mdcg">mdcg</option>
              <option value="imdrf">imdrf</option>
              <option value="team-nb">team-nb</option>
            </select>
          </label>
          <label className="cf-field" style={{ justifyContent: "flex-end" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={autoIngest} style={{ width: "auto" }}
                onChange={(e) => setAutoIngest(e.target.checked)} disabled={uploading || ingesting} />
              Yükleme sonrası otomatik indeksle
            </span>
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <input
            type="file"
            multiple
            accept=".pdf,.csv,.txt,.docx,application/pdf,text/csv,text/plain"
            disabled={uploading}
            onChange={(e) => { void onUploadFiles(e.target.files); e.target.value = ""; }}
          />
        </div>
        {uploading && <p className="muted">Yükleniyor…</p>}
        {uploadLog.length > 0 && (
          <ul style={{ marginTop: 10, fontSize: 13 }}>
            {uploadLog.map((u, i) => (
              <li key={i}><strong>{u.name}</strong> · {(u.size / 1024).toFixed(0)} KB{u.regulation ? ` · ${u.regulation}` : ""}</li>
            ))}
          </ul>
        )}
        <div className="detail-actions">
          <button className="btn-soft" disabled={ingesting || uploading} onClick={() => onIngest(false)}>
            {ingesting ? "İndeksleniyor…" : "Yenileri indeksle"}
          </button>
          <button className="btn-soft" disabled={ingesting || uploading} onClick={() => onIngest(true)}>
            Tümünü sıfırla + indeksle
          </button>
        </div>
        {ingestResult && (
          <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            📦 {ingestResult.indexed}/{ingestResult.files} · 🧩 {ingestResult.chunks} chunk
            {ingestResult.index_reset ? " · 🔄 reset" : ""}
          </div>
        )}
      </div>

      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
          ? sources.filter((s) => s.name.toLowerCase().includes(q) || (s.regulation || "").toLowerCase().includes(q))
          : sources;
        return (
          <>
            <h2 className="section-title">
              Envanter ({loading ? "…" : q ? `${filtered.length}/${sources.length}` : sources.length})
            </h2>
            <div className="data-table-card">
              {/* Diğer admin listeleriyle aynı arama deseni; satır içi style
                  yerine ortak sınıf (design-system: TSX'te elle stil yok). */}
              <div className="data-table-toolbar">
                <label className="data-search">
                  <Icon name="search" size={15} />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Dokümanlarda ara (ad / mevzuat)…"
                    aria-label="Corpus dokümanlarında ara"
                  />
                </label>
                <span className="data-count">
                  {q ? `${filtered.length}/${sources.length}` : sources.length} doküman
                </span>
              </div>
              <div style={{ padding: 16 }}>
              {filtered.length === 0 ? (
                <p className="muted">{sources.length === 0 ? "Doküman yok." : "Eşleşen doküman yok."}</p>
              ) : (
                <ul style={{ fontSize: 13, lineHeight: 1.9, maxHeight: 360, overflowY: "auto", margin: 0 }}>
                  {filtered.map((s, i) => (
                    <li key={i}>
                      <span className="badge ok" style={{ marginRight: 6 }}>{s.type.toUpperCase()}</span>
                      <strong>{s.name}</strong>{s.regulation && <span className="muted"> · {s.regulation}</span>}
                    </li>
                  ))}
                </ul>
              )}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
